import React from 'react';
import { Settings, AlertCircle } from 'lucide-react';

const OptimizationSettingsSection = ({ 
  formData, 
  handleInputChange, 
  errors 
}) => {
  // Обработчик изменения настроек оптимизации
  const handleOptimizationChange = (rule) => {
    handleInputChange({
      target: {
        name: `optimizationRules.${rule}`,
        type: 'checkbox',
        checked: !formData.optimizationRules[rule]
      }
    });
  };
  
  return (
    <div className="space-y-6">
      <h3 className="text-xl font-medium text-gray-900 dark:text-white flex items-center">
        <Settings className="w-5 h-5 mr-2 text-indigo-500" />
        Настройки оптимизации
      </h3>
      
      <div className="bg-gray-50 dark:bg-gray-700/30 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Эти настройки определяют, как система будет оптимизировать расписание. 
          Выберите правила, которые важны для вашего учебного заведения.
        </p>
        
        <div className="space-y-4">
          <label className="flex items-start">
            <input
              type="checkbox"
              checked={formData.optimizationRules.distributeEvenly}
              onChange={() => handleOptimizationChange('distributeEvenly')}
              className="form-checkbox mt-1"
            />
            <div className="ml-2">
              <div className="text-sm font-medium text-gray-800 dark:text-gray-200">
                Равномерное распределение предметов
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Система будет стараться распределить предметы равномерно в течение недели, 
                избегая проведения одного и того же предмета несколько дней подряд.
              </div>
            </div>
          </label>
          
          <label className="flex items-start">
            <input
              type="checkbox"
              checked={formData.optimizationRules.avoidLateStart}
              onChange={() => handleOptimizationChange('avoidLateStart')}
              className="form-checkbox mt-1"
            />
            <div className="ml-2">
              <div className="text-sm font-medium text-gray-800 dark:text-gray-200">
                Избегать позднего начала
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Система будет пытаться начинать занятия как можно раньше, 
                избегая ситуаций, когда первое занятие начинается поздно.
              </div>
            </div>
          </label>
          
          <label className="flex items-start">
            <input
              type="checkbox"
              checked={formData.optimizationRules.prioritizeRoomPreferences}
              onChange={() => handleOptimizationChange('prioritizeRoomPreferences')}
              className="form-checkbox mt-1"
            />
            <div className="ml-2">
              <div className="text-sm font-medium text-gray-800 dark:text-gray-200">
                Учитывать предпочтения по аудиториям
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Система будет стараться назначать занятия в соответствующих аудиториях 
                (например, занятия по информатике в компьютерных классах).
              </div>
            </div>
          </label>
          
          <label className="flex items-start">
            <input
              type="checkbox"
              checked={formData.optimizationRules.balanceLoad}
              onChange={() => handleOptimizationChange('balanceLoad')}
              className="form-checkbox mt-1"
            />
            <div className="ml-2">
              <div className="text-sm font-medium text-gray-800 dark:text-gray-200">
                Сбалансировать нагрузку
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Система будет стараться равномерно распределить количество занятий в день для каждой группы, 
                не допуская перегрузки в один день и недостаточной нагрузки в другой.
              </div>
            </div>
          </label>
        </div>
      </div>
      
      {/* Дополнительные настройки для России */}
      <div className="bg-indigo-50 dark:bg-indigo-900/20 p-4 rounded-lg border border-indigo-100 dark:border-indigo-800">
        <h4 className="text-base font-medium text-indigo-700 dark:text-indigo-300 mb-3">
          Региональные правила
        </h4>
        
        <div className="space-y-4">
          <label className="flex items-start">
            <input
              type="checkbox"
              checked={true}
              disabled
              className="form-checkbox mt-1 opacity-70"
            />
            <div className="ml-2">
              <div className="text-sm font-medium text-indigo-700 dark:text-indigo-300">
                Соблюдение норм СанПиН (Россия)
              </div>
              <div className="text-xs text-indigo-600 dark:text-indigo-400">
                Система автоматически учитывает требования СанПиН для образовательных учреждений России.
                Это включает соблюдение норм дневной нагрузки, чередование сложных и легких предметов,
                и другие установленные санитарные нормы.
              </div>
            </div>
          </label>
          
          <label className="flex items-start">
            <input
              type="checkbox"
              checked={true}
              disabled
              className="form-checkbox mt-1 opacity-70"
            />
            <div className="ml-2">
              <div className="text-sm font-medium text-indigo-700 dark:text-indigo-300">
                Соответствие ФГОС (Россия)
              </div>
              <div className="text-xs text-indigo-600 dark:text-indigo-400">
                Система учитывает требования Федеральных государственных образовательных стандартов
                при составлении расписания, обеспечивая соответствие учебного плана нормативным документам.
              </div>
            </div>
          </label>
        </div>
      </div>
      
      {/* Информация */}
      <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg border border-yellow-100 dark:border-yellow-800">
        <div className="flex items-start">
          <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mr-3 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-yellow-700 dark:text-yellow-300 mb-1">
              Важно знать о составлении расписания
            </h4>
            <p className="text-xs text-yellow-600 dark:text-yellow-400">
              При составлении школьного расписания в России рекомендуется учитывать сложность предметов по дням недели. 
              Наибольшая эффективность обучения достигается на вторник и среду, в пятницу желательно избегать сложных 
              предметов. Система оптимизации автоматически учитывает шкалу сложности предметов по И.Г. Сивкову, 
              которая используется в российских образовательных учреждениях.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OptimizationSettingsSection;